import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './index.css';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ApolloClient, InMemoryCache, ApolloProvider, HttpLink, ApolloLink, from } from '@apollo/client';
import { onError } from "@apollo/client/link/error";

import Navbar from './components/navigation/Navigation';

import Home from './pages/home/App';
import Forums from './pages/forums/App';
import Admin from './pages/admin/App';
import Error403 from './static-pages/403';
import Error404 from './static-pages/404';
import { NotificationProvider } from './hooks/NotificationContext';
import { MessageProvider } from './hooks/MessageContext';
import { authStore } from './store/auth';
import { userStore } from './store/user';
import { messageStore } from './store/message';

import reportWebVitals from './reportWebVitals';
import { ConfigProvider, theme } from 'antd';
import AuthProvider, { useAuth } from './hooks/AuthProvider';

const queryClient = new QueryClient()

const { REACT_APP_API_URL } = process.env;
const httpLink = new HttpLink({ uri: `${REACT_APP_API_URL}/graphql` });

const authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${localStorage.getItem('token')}` || null,
    }
  }));

  return forward(operation);
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);

  if (networkError) {
    if (networkError.statusCode === 403) {
      window.location.href = '/403';
    }

    if (networkError.statusCode === 500) {
      window.location.href = '/500';
    }
    if (networkError.statusCode === 401) {
      const auth = authStore.getState();
      const user = userStore.getState();
      const message = messageStore.getState();

      // logout user
      message.setMessage('Your session has expired. Please login again.');
      auth.handleLogout();
      user.resetUser();
    }
  }
});

const client = new ApolloClient({
  link: from([authMiddleware, errorLink, httpLink]),
  cache: new InMemoryCache(),
});

function PrivateRoute({ children }) {
  const user = useAuth();

  if (user.token) {
    return children;
  } else {
    return <Navigate to="/" />;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: "#23aae7",
          colorInfo: "#1f496b",
          colorSuccess: "#4da564",
          colorError: "#fe3c45",
          colorBgBase: "#0e202f",
          colorPrimaryBg: "#213b4f",
          colorPrimaryActive: "#284358",
          colorPrimaryBgHover: "#224560",
          colorPrimaryBorder: "#284358",
          colorPrimaryBorderHover: "#213b4f",
          colorBgContainer: "#162d3e",
          colorBorder: "#284358",
          colorBorderSecondary: "#284358",
          colorLink: "#23aae7",
          // colorPrimaryBgHover: "#213b4f",
          // colorPrimaryActive: "#218bdf",
          // colorPrimaryBg: "#213b4f",
          // colorPrimary: "#218bdf",
          // colorInfo: "#213b4f",
          // colorSuccess: "#4da564",
          // colorError: "#fe3c45",
          // colorBgBase: "#0e202f",
          // colorBgContainer: "#162d3e",
          // colorBorder: "#284358",
          // colorBorderBg: "#213b4f",
          // colorBorderSecondary: "#213b4f",
        },
      }}
    >
      <div className="background-image"></div>
      <NotificationProvider>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider client={client}>
            <MessageProvider>
              <Router>
                <AuthProvider>
                  <Navbar>
                    <Routes>
                      <Route index path="/" element={<Home />} />
                      <Route path="/403" element={<Error403 />} />
                      <Route path="/forums/*" element={<PrivateRoute><Forums /></PrivateRoute>} />
                      <Route path="/admin/*" element={<PrivateRoute><Admin /></PrivateRoute>} />
                      <Route path="*" element={<Error404 />} />
                    </Routes>
                  </Navbar>
                </AuthProvider>
              </Router>
            </MessageProvider>
          </ApolloProvider>
        </QueryClientProvider>
      </NotificationProvider>
    </ConfigProvider>
  </React.StrictMode>
);

reportWebVitals();
